import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { TenantService } from '../../../core/tenant/tenant.service';
import type {
  WhatsAppCapabilities,
  WhatsAppOverview,
  WhatsAppRecentActivityItem,
  WhatsAppTemplate,
} from '../models/whatsapp-settings.models';
import { WhatsAppSettingsApiService } from '../services/whatsapp-settings-api.service';

@Injectable({ providedIn: 'root' })
export class WhatsAppSettingsStore {
  private readonly tenant = inject(TenantService);
  private readonly api = inject(WhatsAppSettingsApiService);

  private readonly loadedCompanyIdSignal = signal<string | null>(null);

  private readonly overviewSignal = signal<WhatsAppOverview | null>(null);
  private readonly templatesSignal = signal<WhatsAppTemplate[]>([]);
  private readonly capabilitiesSignal = signal<WhatsAppCapabilities | null>(null);
  private readonly recentActivitySignal = signal<WhatsAppRecentActivityItem[]>([]);

  private readonly loadingOverviewSignal = signal(false);
  private readonly loadingTemplatesSignal = signal(false);
  private readonly loadingCapabilitiesSignal = signal(false);
  private readonly loadingActivitySignal = signal(false);

  private readonly errorOverviewSignal = signal<string | null>(null);
  private readonly errorTemplatesSignal = signal<string | null>(null);
  private readonly errorCapabilitiesSignal = signal<string | null>(null);
  private readonly errorActivitySignal = signal<string | null>(null);

  readonly overview = this.overviewSignal.asReadonly();
  readonly templates = this.templatesSignal.asReadonly();
  readonly capabilities = this.capabilitiesSignal.asReadonly();
  readonly recentActivity = this.recentActivitySignal.asReadonly();

  readonly loadingOverview = this.loadingOverviewSignal.asReadonly();
  readonly loadingTemplates = this.loadingTemplatesSignal.asReadonly();
  readonly loadingCapabilities = this.loadingCapabilitiesSignal.asReadonly();
  readonly loadingActivity = this.loadingActivitySignal.asReadonly();

  readonly errorOverview = this.errorOverviewSignal.asReadonly();
  readonly errorTemplates = this.errorTemplatesSignal.asReadonly();
  readonly errorCapabilities = this.errorCapabilitiesSignal.asReadonly();
  readonly errorActivity = this.errorActivitySignal.asReadonly();

  readonly isConfigured = computed(() => this.overview()?.isConfigured ?? false);
  readonly canSendMessages = computed(() => this.overview()?.canSendMessages ?? false);
  readonly templatesCount = computed(() => this.overview()?.templatesCount ?? this.templates().length);
  readonly hasTemplates = computed(() => this.templates().length > 0);
  readonly hasRecentActivity = computed(() => this.recentActivity().length > 0);

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.loadedCompanyIdSignal.set(null);
        this.reset();
        return;
      }
      if (this.loadedCompanyIdSignal() === companyId) return;
      this.reset();
      this.loadedCompanyIdSignal.set(companyId);
      this.loadAll();
    });
  }

  loadAll(): void {
    this.loadOverview();
    this.loadTemplates();
    this.loadCapabilities();
    this.loadRecentActivity();
  }

  loadOverview(): void {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingOverviewSignal.set(true);
    this.errorOverviewSignal.set(null);
    void this.api
      .getOverview()
      .then((overview) => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.overviewSignal.set(overview);
      })
      .catch(() => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.overviewSignal.set(null);
        this.errorOverviewSignal.set('settings.communication.blocks.overview.error');
      })
      .finally(() => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.loadingOverviewSignal.set(false);
      });
  }

  loadTemplates(): void {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingTemplatesSignal.set(true);
    this.errorTemplatesSignal.set(null);
    void this.api
      .getTemplates()
      .then((templates) => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.templatesSignal.set(templates);
      })
      .catch(() => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.templatesSignal.set([]);
        this.errorTemplatesSignal.set('settings.communication.blocks.templates.error');
      })
      .finally(() => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.loadingTemplatesSignal.set(false);
      });
  }

  loadCapabilities(): void {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingCapabilitiesSignal.set(true);
    this.errorCapabilitiesSignal.set(null);
    void this.api
      .getCapabilities()
      .then((capabilities) => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.capabilitiesSignal.set(capabilities);
      })
      .catch(() => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.capabilitiesSignal.set(null);
        this.errorCapabilitiesSignal.set('settings.communication.blocks.capabilities.error');
      })
      .finally(() => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.loadingCapabilitiesSignal.set(false);
      });
  }

  loadRecentActivity(): void {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingActivitySignal.set(true);
    this.errorActivitySignal.set(null);
    void this.api
      .getRecentActivity()
      .then((recentActivity) => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.recentActivitySignal.set(recentActivity);
      })
      .catch(() => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.recentActivitySignal.set([]);
        this.errorActivitySignal.set('settings.communication.blocks.activity.error');
      })
      .finally(() => {
        if (!this.isSameCompany(requestCompanyId)) return;
        this.loadingActivitySignal.set(false);
      });
  }

  reset(): void {
    this.loadedCompanyIdSignal.set(null);
    this.overviewSignal.set(null);
    this.templatesSignal.set([]);
    this.capabilitiesSignal.set(null);
    this.recentActivitySignal.set([]);
    this.loadingOverviewSignal.set(false);
    this.loadingTemplatesSignal.set(false);
    this.loadingCapabilitiesSignal.set(false);
    this.loadingActivitySignal.set(false);
    this.errorOverviewSignal.set(null);
    this.errorTemplatesSignal.set(null);
    this.errorCapabilitiesSignal.set(null);
    this.errorActivitySignal.set(null);
  }

  private isSameCompany(requestCompanyId: string): boolean {
    return this.tenant.companyId() === requestCompanyId;
  }
}
